import { useCallback, useMemo } from 'react';
import { SettingsActionDispatcher } from '../reducer/settings-tree.reducer';
import { ISettingsNode } from '../mappings/build-settings-tree';
import { mapSettingsTreeToProps } from '../mappings/map-settings-tree-to-props';
import { SettingsPanelProps } from '../components/settings-tree-panel/settings-tree-panel.component';
import {
  makeDefineSettingsActionsButtonActions,
  SettingsActionsButtonActions
} from '../actions-button/settings-actions';
import {
  KnownProjectDtoProjectType,
  KnownSettingsNodeDtoStatus,
  KnownSettingsNodeDtoType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { RelationshipType, SettingsState, SettingsTreeDataRequestType } from '../reducer/settings-tree-reducer.types';
import { findAllDescendants } from '../reducer/settings-tree-reducer-helpers';
import { useQueryGetProjectOptional } from '../../../core/queries/projects.queries';
import { useQueryGetCarrierHierarchy } from '../../carriers/carriers.queries';
import { getProjectCapabilities } from '../logic/project-capabilities';

/**
 * Maps the settings tree data to the props required by the SettingsTree component.
 * Memoizes the tree props to avoid unnecessary re-renders.
 * @param rootSettingsTreeNode
 * @param dispatch
 * @param state
 * @returns SettingsTreeProps
 */
export function useSettingsTreePanelProps(
  rootSettingsTreeNode: ISettingsNode | undefined,
  dispatch: SettingsActionDispatcher,
  state: SettingsState
): SettingsPanelProps | null {
  // Memoize the tree props to avoid unnecessary re-renders.
  const tree = useMemo(() => {
    if (!rootSettingsTreeNode) {
      return null;
    }
    return mapSettingsTreeToProps(rootSettingsTreeNode, dispatch);
  }, [rootSettingsTreeNode, dispatch]);

  const registrationPageExists = useMemo(() => {
    if (!state.selectedNode) {
      return false;
    }
    if (state.selectedNode.type !== KnownSettingsNodeDtoType.Project) {
      return false;
    }
    const exists = state.projectChildrenTree.nodes.some((node) => {
      return (
        node.type === KnownSettingsNodeDtoType.RegistrationPage && node.request.rootId === state.selectedNode?.entityId
      );
    });
    return exists;
  }, [state.projectChildrenTree.nodes, state.selectedNode]);

  const accessControlExists = useMemo(() => {
    if (!state.selectedNode) {
      return false;
    }
    const { type, id } = state.selectedNode;
    if (type !== KnownSettingsNodeDtoType.Project && type !== KnownSettingsNodeDtoType.Hub) {
      return false;
    }
    const allNodes = [...state.projectChildrenTree.nodes, ...state.placeholderNodes];
    return allNodes.some(
      (node) =>
        node.type === KnownSettingsNodeDtoType.AccessControl &&
        node.parentId === id &&
        node.status !== KnownSettingsNodeDtoStatus.Deleted
    );
  }, [state.projectChildrenTree.nodes, state.placeholderNodes, state.selectedNode]);

  const nodeRelationship = useMemo(() => {
    if (!state.selectedNode || state.selectedNode.type !== KnownSettingsNodeDtoType.Project) {
      return { relationshipType: RelationshipType.Unknown, relatedNodeId: undefined };
    }
    const { parentId } = state.selectedNode;
    const allNodes = [...state.projectChildrenTree.nodes, ...state.placeholderNodes];
    const linkFolderNode = allNodes.find((n) => n.id === parentId);
    if (!linkFolderNode || linkFolderNode.type !== KnownSettingsNodeDtoType.ProjectLinkFolder) {
      return { relationshipType: RelationshipType.Unknown, relatedNodeId: undefined };
    }
    // The parent of the link folder is the related entity
    const relatedNode = allNodes.find((n) => n.id === linkFolderNode.parentId);
    const relationshipType = linkFolderNode.entityId.includes('accessed-via')
      ? RelationshipType.AccessedVia
      : RelationshipType.Unknown;
    return { relationshipType, relatedNodeId: relatedNode?.id, relatedEntityId: relatedNode?.entityId };
  }, [state.selectedNode, state.projectChildrenTree.nodes, state.placeholderNodes]);

  const { relationshipType: nodeRelationshipType, relatedEntityId } = nodeRelationship;

  // The tree node carries no feature flags, so read the project itself.
  const selectedProjectId =
    state.selectedNode?.type === KnownSettingsNodeDtoType.Project && !state.selectedNode.isPlaceholder
      ? state.selectedNode.entityId
      : undefined;
  const selectedProject = useQueryGetProjectOptional(selectedProjectId, { enabled: !!selectedProjectId });

  // Only a carrier project can gain a region, and the hierarchy is what says whether it manages an
  // organisation yet — the backend enforces the same precondition.
  const isCarrierProject = selectedProject.data?.projectType === KnownProjectDtoProjectType.Carrier;
  const carrierHierarchy = useQueryGetCarrierHierarchy(selectedProjectId, { enabled: isCarrierProject });
  const capabilities = useMemo(
    () => getProjectCapabilities(selectedProject.data, carrierHierarchy.data),
    [selectedProject.data, carrierHierarchy.data]
  );

  const setAction = useCallback(
    (action: SettingsActionsButtonActions, payload?: any) => {
      if (!state.selectedNode) {
        return;
      }
      switch (action) {
        case SettingsActionsButtonActions.CREATE_PROJECT:
          dispatch({
            type: 'CREATE_NODE',
            payload: {
              nodeType: KnownSettingsNodeDtoType.Project,
              requestType: SettingsTreeDataRequestType.Projects,
              parentNodeId: state.selectedNode?.id
            }
          });
          break;
        case SettingsActionsButtonActions.ASSIGN_NETWORKS:
          if (capabilities.carrierOrganisationGroupID) {
            dispatch({
              type: 'SET_CURRENT_ACTION',
              payload: {
                type: 'ASSIGN_NETWORKS',
                organisationGroupId: capabilities.carrierOrganisationGroupID,
                carrierName: state.selectedNode.displayLabel
              }
            });
          }
          break;
        case SettingsActionsButtonActions.ADD_REGION:
          dispatch({
            type: 'SET_CURRENT_ACTION',
            payload: {
              type: 'ADD_REGION',
              carrierProjectId: state.selectedNode.entityId,
              carrierProjectName: state.selectedNode.displayLabel,
              parentNodeId: state.selectedNode.id
            }
          });
          break;
        case SettingsActionsButtonActions.ADD_CARRIER:
          dispatch({
            type: 'SET_CURRENT_ACTION',
            payload: {
              type: 'ADD_CARRIER',
              parentProjectId: state.selectedNode.entityId,
              parentNodeId: state.selectedNode.id
            }
          });
          break;
        case SettingsActionsButtonActions.ADD_PROJECT_MODULE:
          dispatch({
            type: 'CREATE_NODE',
            payload: {
              nodeType: KnownSettingsNodeDtoType.ProjectModule,
              requestType: SettingsTreeDataRequestType.ProjectChildren,
              parentNodeId: state.selectedNode?.id
            }
          });
          break;
        case SettingsActionsButtonActions.ADD_HUB:
          dispatch({
            type: 'CREATE_NODE',
            payload: {
              nodeType: KnownSettingsNodeDtoType.Hub,
              requestType: SettingsTreeDataRequestType.ProjectChildren,
              parentNodeId: state.selectedNode?.id
            }
          });
          break;
        case SettingsActionsButtonActions.ADD_REGISTRATION_PAGE:
          dispatch({
            type: 'CREATE_NODE',
            payload: {
              nodeType: KnownSettingsNodeDtoType.RegistrationPage,
              requestType: SettingsTreeDataRequestType.ProjectChildren,
              parentNodeId: state.selectedNode?.id
            }
          });
          break;
        case SettingsActionsButtonActions.ADD_ACCESS_CONTROL:
          dispatch({
            type: 'CREATE_NODE',
            payload: {
              nodeType: KnownSettingsNodeDtoType.AccessControl,
              requestType: SettingsTreeDataRequestType.ProjectChildren,
              parentNodeId: state.selectedNode?.id
            }
          });
          break;
        case SettingsActionsButtonActions.DELETE_PROJECT:
          const childProjects = findAllDescendants(
            state.selectedNode,
            state.projectTree,
            KnownSettingsNodeDtoType.Project
          );
          dispatch({
            type: 'SET_CURRENT_ACTION',
            payload: {
              type: 'DELETE_NODE',
              action: {
                nodeId: state.selectedNode.id,
                entityId: state.selectedNode.entityId,
                parentNodeId: state.selectedNode.parentId,
                nodeType: KnownSettingsNodeDtoType.Project,
                requestType: SettingsTreeDataRequestType.Projects,
                childNodeIds: childProjects,
                projectId: state.selectedNode.id
              }
            }
          });
          break;
        case SettingsActionsButtonActions.DELETE_PROJECT_MODULE:
          dispatch({
            type: 'SET_CURRENT_ACTION',
            payload: {
              type: 'DELETE_NODE',
              action: {
                nodeId: state.selectedNode.id,
                entityId: state.selectedNode.entityId,
                parentNodeId: state.selectedNode.parentId,
                nodeType: KnownSettingsNodeDtoType.ProjectModule,
                requestType: SettingsTreeDataRequestType.ProjectChildren,
                childNodeIds: [],
                projectId: state.selectedNode.request.rootId
              }
            }
          });
          break;
        case SettingsActionsButtonActions.DELETE_REGISTRATION_PAGE:
          dispatch({
            type: 'SET_CURRENT_ACTION',
            payload: {
              type: 'DELETE_NODE',
              action: {
                nodeId: state.selectedNode.id,
                entityId: state.selectedNode.entityId,
                parentNodeId: state.selectedNode.parentId,
                nodeType: KnownSettingsNodeDtoType.RegistrationPage,
                requestType: SettingsTreeDataRequestType.ProjectChildren,
                childNodeIds: [],
                projectId: state.selectedNode.request.rootId
              }
            }
          });
          break;
        case SettingsActionsButtonActions.DELETE_ACCESS_CONTROL:
          dispatch({
            type: 'SET_CURRENT_ACTION',
            payload: {
              type: 'DELETE_NODE',
              action: {
                nodeId: state.selectedNode.id,
                entityId: state.selectedNode.entityId,
                parentNodeId: state.selectedNode.parentId,
                nodeType: KnownSettingsNodeDtoType.AccessControl,
                requestType: SettingsTreeDataRequestType.ProjectChildren,
                childNodeIds: [],
                projectId: state.selectedNode.request.rootId,
                label: state.selectedNode.displayLabel
              }
            }
          });
          break;
        case SettingsActionsButtonActions.DELETE_HUB:
          dispatch({
            type: 'SET_CURRENT_ACTION',
            payload: {
              type: 'DELETE_NODE',
              action: {
                nodeId: state.selectedNode.id,
                entityId: state.selectedNode.entityId,
                parentNodeId: state.selectedNode.parentId ?? undefined,
                requestType: SettingsTreeDataRequestType.ProjectChildren,
                childNodeIds: [],
                nodeType: KnownSettingsNodeDtoType.Hub,
                label: state.selectedNode.displayLabel
              }
            }
          });
          break;
        case SettingsActionsButtonActions.DELETE_CARRIER:
          dispatch({
            type: 'SET_CURRENT_ACTION',
            payload: {
              type: 'DELETE_NODE',
              action: {
                nodeId: state.selectedNode.id,
                entityId: state.selectedNode.entityId,
                parentNodeId: state.selectedNode.parentId ?? undefined,
                requestType: state.selectedNode.request.type,
                childNodeIds: [],
                nodeType: KnownSettingsNodeDtoType.Group,
                label: state.selectedNode.displayLabel
              }
            }
          });
          break;
        case SettingsActionsButtonActions.ADD_NEW_RELATIONSHIP:
          dispatch({
            type: 'CREATE_NODE',
            payload: {
              nodeType: KnownSettingsNodeDtoType.ProjectLinkFolder,
              requestType: SettingsTreeDataRequestType.ProjectChildren,
              parentNodeId: state.selectedNode.id,
              data: { relationshipType: (payload as RelationshipType) ?? RelationshipType.Unknown }
            }
          });
          break;
        case SettingsActionsButtonActions.DELETE_RELATIONSHIP:
          dispatch({
            type: 'SET_CURRENT_ACTION',
            payload: {
              type: 'DELETE_RELATIONSHIP',
              relatedEntityId: relatedEntityId,
              selectedEntityId: state.selectedNode.entityId,
              relationshipType: nodeRelationshipType
            }
          });
          break;
        case SettingsActionsButtonActions.APPLY_KIOSK_THEME_PRESET:
          if (state.selectedNode.parentEntityId) {
            dispatch({
              type: 'SET_CURRENT_ACTION',
              payload: {
                type: 'APPLY_KIOSK_THEME_PRESET',
                nodeId: state.selectedNode.id,
                kioskId: state.selectedNode.parentEntityId,
                projectId: state.selectedNode.request.rootId
              }
            });
          }
          break;
        case SettingsActionsButtonActions.OPEN_REGISTRATION_PAGE:
          if (state.selectedNode.type === KnownSettingsNodeDtoType.RegistrationPage) {
            dispatch({
              type: 'SET_CURRENT_ACTION',
              payload: {
                type: 'OPEN_REGISTRATION_PAGE',
                registrationPageId: state.selectedNode.entityId,
                projectId: state.selectedNode.request.rootId
              }
            });
          }
          break;
        default:
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, state.selectedNode, capabilities]
  );

  const actionButtonActions = useMemo(() => {
    return makeDefineSettingsActionsButtonActions(
      state.selectedNode,
      state.projectChildrenTree,
      registrationPageExists,
      accessControlExists,
      capabilities
    )(setAction);
  }, [
    setAction,
    state.selectedNode,
    state.projectChildrenTree,
    registrationPageExists,
    accessControlExists,
    capabilities
  ]);

  const onToggleNode = useCallback(
    (nodeIds: string[]) => {
      dispatch({ type: 'TOGGLE_NODE', payload: { nodeIds } });
    },
    [dispatch]
  );

  if (!tree) {
    return null;
  }

  return {
    title: 'Project Hierarchy',
    root: tree,
    // We must provide a default value for selectedNodeId, because the TreeView component
    // will be uncontrolled if the selectedNodeId is undefined.
    selectedNodeId: state.selectedNode?.id || rootSettingsTreeNode?.id,
    actionButtonActions,
    expandedNodeIds: Array.from(state.expandedNodeIds),
    onToggleNode
  };
}
